import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Card } from 'primereact/card';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Messages } from 'primereact/messages';
import { ProgressBar } from 'primereact/progressbar';
import Container from 'react-bootstrap/Container';
import walletPortalApi from '../api/walletPortalApi';
import './StandaloneWalletPassPortal.css';

const SESSION_KEY = 'wallet-pass-portal-session';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACCESS_POINT_ROW_BATCH_SIZE = 3;
const DEFAULT_DURATION_SHORTCUTS = [
  { id: '30-minutes', label: '30 minutes', minutes: 30 },
  { id: '1-hour', label: '1 hour', minutes: 60 },
  { id: '1-day', label: '1 day', minutes: 1440 },
];
const emptyForm = () => ({
  email: '',
  isMultiUse: false,
  maxUses: 1,
  durationMinutes: 0,
  durationHours: 0,
  durationDays: 0,
  memberNumber: '',
});
const durationParts = (totalMinutes) => {
  const value = Number(totalMinutes) || 0;
  return {
    days: Math.floor(value / 1440),
    hours: Math.floor((value % 1440) / 60),
    minutes: value % 60,
  };
};
const durationInMinutes = (duration) =>
  Number(duration.durationMinutes || 0) +
  Number(duration.durationHours || 0) * 60 +
  Number(duration.durationDays || 0) * 1440;
const errorMessage = (error, fallback) => {
  const data = error && error.response && error.response.data;
  return typeof data === 'string' && data.trim()
    ? data
    : (data && (data.detail || data.title)) || (error && error.message) || fallback;
};

const StandaloneWalletPassPortal = () => {
  const msgs = useRef(null);
  const accessPointsListRef = useRef(null);
  const [session, setSession] = useState(null);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [availability, setAvailability] = useState(null);
  const [accessPoints, setAccessPoints] = useState([]);
  const [selectedAccessPointIds, setSelectedAccessPointIds] = useState([]);
  const [visibleAccessPointRows, setVisibleAccessPointRows] = useState(ACCESS_POINT_ROW_BATCH_SIZE);
  const [accessPointColumnCount, setAccessPointColumnCount] = useState(1);
  const [form, setForm] = useState(emptyForm());
  const [createdPass, setCreatedPass] = useState(null);
  const [customShortcuts, setCustomShortcuts] = useState([]);
  const [showShortcutDialog, setShowShortcutDialog] = useState(false);
  const [createPassDialog, setCreatePassDialog] = useState(null);
  const [shortcutName, setShortcutName] = useState('');
  const maxUsesLimit = availability && availability.limitByMaxUses ? Number(availability.maxUsesLimit) : null;
  const maxDurationMinutes = availability && availability.limitByMaxDuration ? Number(availability.maxDurationMinutes) : null;
  const maximumDuration = durationParts(maxDurationMinutes);
  const requestedDurationMinutes = durationInMinutes(form);
  const shortcutStorageKey = session ? `wallet-pass-duration-shortcuts-${session.locationId}` : null;
  const availableAccessPointIds = accessPoints.map((accessPoint) => Number(accessPoint.id)).filter(Number.isFinite);
  const areAllAccessPointsSelected = availableAccessPointIds.length > 0 && availableAccessPointIds.every((accessPointId) => selectedAccessPointIds.indexOf(accessPointId) !== -1);
  const accessPointRowCount = Math.ceil(accessPoints.length / accessPointColumnCount);
  const hasMoreAccessPointRows = accessPointRowCount > visibleAccessPointRows;
  const canShowFewerAccessPointRows = visibleAccessPointRows > ACCESS_POINT_ROW_BATCH_SIZE;
  const availableShortcuts = DEFAULT_DURATION_SHORTCUTS.concat(customShortcuts).filter((shortcut) => !Number.isFinite(maxDurationMinutes) || maxDurationMinutes <= 0 || shortcut.minutes <= maxDurationMinutes);
  const accessPointList = accessPoints.length === 0 ? (
    <small>No access points are available for this location.</small>
  ) : (
    <div ref={accessPointsListRef} className="wallet-access-points" style={{ maxHeight: `${visibleAccessPointRows * 3.75 - 0.5}rem` }}>
      {accessPoints.map((accessPoint) => {
        const accessPointId = Number(accessPoint.id);
        const label = String(accessPoint.gate || accessPoint.tile || `Access point ${accessPointId}`);
        return (
          <div key={accessPointId} className="wallet-access-point">
            <Checkbox inputId={`wallet-access-point-${accessPointId}`} checked={selectedAccessPointIds.indexOf(accessPointId) !== -1} onChange={(event) => setSelectedAccessPointIds((current) => event.checked ? current.concat(accessPointId) : current.filter((id) => id !== accessPointId))} />
            <label htmlFor={`wallet-access-point-${accessPointId}`}>{label}</label>
          </div>
        );
      })}
    </div>
  );

  const clearSession = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setSession(null);
    setAvailability(null);
    setAccessPoints([]);
    setSelectedAccessPointIds([]);
    setCreatedPass(null);
    setForm(emptyForm());
  };

  const loadPortal = async (activeSession) => {
    try {
      setPortalLoading(true);
      const [nextAvailability, nextAccessPoints] = await Promise.all([
        walletPortalApi.getAvailability(activeSession.locationId, activeSession.token),
        walletPortalApi.getAccessPoints(activeSession.locationId, activeSession.token),
      ]);
      setAvailability(nextAvailability);
      setAccessPoints(Array.isArray(nextAccessPoints) ? nextAccessPoints : []);
      setVisibleAccessPointRows(ACCESS_POINT_ROW_BATCH_SIZE);
    } catch (error) {
      msgs.current.show({ severity: 'error', summary: '', detail: errorMessage(error, 'Unable to load the Wallet Pass Portal.'), sticky: true });
      if (error && error.response && (error.response.status === 401 || error.response.status === 403)) clearSession();
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(SESSION_KEY) || 'null');
      if (saved && saved.token && Number(saved.locationId) > 0) {
        setSession(saved);
        loadPortal(saved);
      }
    } catch (_) {
      sessionStorage.removeItem(SESSION_KEY);
    }
  }, []);

  useEffect(() => {
    if (!shortcutStorageKey) return;
    try {
      const saved = JSON.parse(localStorage.getItem(shortcutStorageKey) || '[]');
      setCustomShortcuts(Array.isArray(saved) ? saved.filter((shortcut) => shortcut && typeof shortcut.label === 'string' && Number(shortcut.minutes) > 0) : []);
    } catch (_) {
      setCustomShortcuts([]);
    }
  }, [shortcutStorageKey]);

  useEffect(() => {
    const updateAccessPointColumnCount = () => {
      const list = accessPointsListRef.current;
      if (!list) return;
      const columns = window.getComputedStyle(list).gridTemplateColumns.split(' ').filter((track) => Number.parseFloat(track) > 0).length;
      setAccessPointColumnCount(Math.max(1, columns));
    };
    updateAccessPointColumnCount();
    window.addEventListener('resize', updateAccessPointColumnCount);
    return () => window.removeEventListener('resize', updateAccessPointColumnCount);
  }, [accessPoints, portalLoading]);

  const startSession = async () => {
    const trimmedCode = String(code || '').trim();
    if (!trimmedCode) {
      msgs.current.show({ severity: 'error', summary: '', detail: 'Enter a portal code.' });
      return;
    }
    try {
      setLoading(true);
      const nextSession = await walletPortalApi.createSession(trimmedCode);
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(nextSession));
      setSession(nextSession);
      setCode('');
      await loadPortal(nextSession);
    } catch (error) {
      msgs.current.show({ severity: 'error', summary: '', detail: errorMessage(error, 'The portal code is invalid, disabled, or expired.') });
    } finally {
      setLoading(false);
    }
  };

  const applyDuration = (minutes) => {
    const parts = durationParts(minutes);
    setForm({ ...form, durationMinutes: parts.minutes, durationHours: parts.hours, durationDays: parts.days });
  };

  const saveShortcut = () => {
    const label = String(shortcutName || '').trim();
    if (!label || requestedDurationMinutes <= 0 || !shortcutStorageKey) return;
    const next = customShortcuts.concat({ id: String(Date.now()), label, minutes: requestedDurationMinutes });
    localStorage.setItem(shortcutStorageKey, JSON.stringify(next));
    setCustomShortcuts(next);
    setShortcutName('');
    setShowShortcutDialog(false);
  };

  const showCreatePassDialog = (severity, message) =>
    setCreatePassDialog({ severity, message });

  const createPass = async () => {
    if (!EMAIL_PATTERN.test(String(form.email || '').trim())) return showCreatePassDialog('error', 'Enter a valid email address.');
    if (selectedAccessPointIds.length === 0) return showCreatePassDialog('error', 'At least one access point must be selected.');
    if (!Number.isInteger(requestedDurationMinutes) || requestedDurationMinutes <= 0) return showCreatePassDialog('error', 'Maximum usage time must be greater than zero.');
    if (form.isMultiUse && Number(form.maxUses) <= 1) return showCreatePassDialog('error', 'Multi-use passes must have more than one use.');
    if (Number.isFinite(maxUsesLimit) && maxUsesLimit > 0 && Number(form.maxUses) > maxUsesLimit) return showCreatePassDialog('error', `Number of uses cannot exceed ${maxUsesLimit}.`);
    if (Number.isFinite(maxDurationMinutes) && maxDurationMinutes > 0 && requestedDurationMinutes > maxDurationMinutes) return showCreatePassDialog('error', 'Maximum usage time cannot exceed the maximum allowed duration.');
    try {
      setLoading(true);
      const memberNumber = String(form.memberNumber || '').trim();
      const created = await walletPortalApi.createPass(session.locationId, session.token, {
        email: form.email.trim(),
        isMultiUse: form.isMultiUse,
        maxUses: form.isMultiUse ? Number(form.maxUses) : 1,
        expiresAtUtc: new Date(Date.now() + requestedDurationMinutes * 60000).toISOString(),
        overrideLms: !memberNumber,
        memberNumber: memberNumber || null,
        accessPointIds: selectedAccessPointIds,
      });
      setCreatedPass(created);
      setForm(emptyForm());
      setSelectedAccessPointIds([]);
      showCreatePassDialog('success', 'Wallet pass created successfully.');
    } catch (error) {
      showCreatePassDialog('error', errorMessage(error, 'Failed to create wallet pass.'));
    } finally {
      setLoading(false);
    }
  };

  const copyPassUrl = () => {
    if (createdPass && createdPass.passUrl) {
      navigator.clipboard.writeText(createdPass.passUrl);
      msgs.current.show({ severity: 'success', summary: '', detail: 'Pass link copied to clipboard.' });
    }
  };

  if (!session) {
    return <main className="wallet-portal-login"><Card><h1>Smart Tile</h1><h2>Wallet Pass Portal</h2><Messages ref={msgs} /><div className="p-fluid"><label htmlFor="walletPortalCode">Portal Code</label><InputText id="walletPortalCode" value={code} onChange={(event) => setCode(event.target.value)} onKeyDown={(event) => event.key === 'Enter' && startSession()} /><Button label="Continue" icon="pi pi-arrow-right" onClick={startSession} loading={loading} /></div></Card></main>;
  }

  return (
    <main className="wallet-portal-page">
      <Card className="wallet-portal-card">
        <Container fluid="md">
          <div className="wallet-portal-title"><h4>Wallet Passes</h4><Button label="Change code" icon="pi pi-sign-out" className="p-button-text" onClick={clearSession} /></div>
          <Messages ref={msgs} />
          {portalLoading ? <ProgressBar mode="indeterminate" style={{ height: '6px' }} /> : !availability || !availability.isEnabled ? <p>Wallet passes are not enabled for this location.</p> : <>
            <div className="p-fluid p-formgrid p-grid wallet-portal-form-grid">
              <div className="p-field p-col-12 p-md-6"><label htmlFor="walletEmail">Email address</label><InputText id="walletEmail" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} /></div>
              <div className="p-field p-col-12 p-md-6"><label htmlFor="walletType">Pass type</label><Dropdown id="walletType" value={form.isMultiUse} options={[{ label: 'Single use', value: false }, { label: 'Multi use', value: true, disabled: Number.isFinite(maxUsesLimit) && maxUsesLimit < 2 }]} optionDisabled="disabled" onChange={(event) => setForm({ ...form, isMultiUse: event.value, maxUses: event.value ? 2 : 1 })} /></div>
              {Number.isFinite(maxUsesLimit) && maxUsesLimit > 0 && <div className="p-col-12"><small>Maximum allowed uses: {maxUsesLimit}</small></div>}
              {form.isMultiUse && <div className="p-field p-col-12 p-md-6"><label htmlFor="walletUses">Number of passes/uses</label><InputNumber id="walletUses" value={form.maxUses} min={2} max={Number.isFinite(maxUsesLimit) && maxUsesLimit > 0 ? maxUsesLimit : undefined} minFractionDigits={0} maxFractionDigits={0} useGrouping={false} onValueChange={(event) => setForm({ ...form, maxUses: event.value })} /></div>}
              <div className="p-field p-col-12 wallet-access-points-field">
                <div className="wallet-access-points-header"><label>Access points</label><Button label={areAllAccessPointsSelected ? 'Unselect all' : 'Select all'} className="p-button-text p-button-sm" onClick={() => setSelectedAccessPointIds(areAllAccessPointsSelected ? [] : availableAccessPointIds)} disabled={accessPoints.length === 0} /></div>
                {accessPointList}
                {(hasMoreAccessPointRows || canShowFewerAccessPointRows) && (
                  <div className="wallet-access-points-actions">
                    {hasMoreAccessPointRows && (
                      <Button label="Show more" className="p-button-text p-button-sm wallet-access-points-toggle" onClick={() => setVisibleAccessPointRows((current) => current + ACCESS_POINT_ROW_BATCH_SIZE)} />
                    )}
                    {canShowFewerAccessPointRows && (
                      <Button label="Show less" className="p-button-text p-button-sm wallet-access-points-toggle" onClick={() => setVisibleAccessPointRows(ACCESS_POINT_ROW_BATCH_SIZE)} />
                    )}
                  </div>
                )}
                <small className="wallet-access-points-hint">Select at least one access point.</small>
              </div>
              <div className="p-field p-col-12 wallet-duration-field">
                <label>Maximum usage time</label>
                <div className="wallet-duration-row">
                  <div className="wallet-duration-input"><InputNumber id="walletDurationMinutes" value={form.durationMinutes} min={0} minFractionDigits={0} maxFractionDigits={0} useGrouping={false} onValueChange={(event) => setForm({ ...form, durationMinutes: event.value })} /><label htmlFor="walletDurationMinutes">minutes</label></div>
                  <div className="wallet-duration-input"><InputNumber id="walletDurationHours" value={form.durationHours} min={0} minFractionDigits={0} maxFractionDigits={0} useGrouping={false} onValueChange={(event) => setForm({ ...form, durationHours: event.value })} /><label htmlFor="walletDurationHours">hours</label></div>
                  <div className="wallet-duration-input"><InputNumber id="walletDurationDays" value={form.durationDays} min={0} minFractionDigits={0} maxFractionDigits={0} useGrouping={false} onValueChange={(event) => setForm({ ...form, durationDays: event.value })} /><label htmlFor="walletDurationDays">days</label></div>
                  <Button label="Save as shortcut" icon="pi pi-bookmark" className="p-button-text p-button-sm wallet-save-shortcut" onClick={() => setShowShortcutDialog(true)} />
                </div>
                {Number.isFinite(maxDurationMinutes) && maxDurationMinutes > 0 && <small className="wallet-duration-limit">Maximum allowed duration: {maximumDuration.days} days {maximumDuration.hours} hours {maximumDuration.minutes} minutes</small>}
                <div className="wallet-duration-shortcuts">{availableShortcuts.map((shortcut) => <Button key={shortcut.id} label={shortcut.label} className="p-button-outlined p-button-sm" onClick={() => applyDuration(shortcut.minutes)} />)}</div>
              </div>
              <div className="p-field p-col-12 p-md-6"><label htmlFor="walletMember">Add member/card number, if not a member, leave empty</label><InputText id="walletMember" value={form.memberNumber} onChange={(event) => setForm({ ...form, memberNumber: event.target.value })} /></div>
              <div className="p-col-12"><Button label="Create Wallet Pass" icon="pi pi-plus" onClick={createPass} loading={loading} disabled={selectedAccessPointIds.length === 0} /></div>
            </div>
          </>}
          <Dialog header="Save Time Shortcut" visible={showShortcutDialog} onHide={() => setShowShortcutDialog(false)} footer={<><Button label="Cancel" className="p-button-text" onClick={() => setShowShortcutDialog(false)} /><Button label="Save" icon="pi pi-save" onClick={saveShortcut} /></>}><div className="p-fluid"><label htmlFor="walletShortcutName">Shortcut name</label><InputText id="walletShortcutName" value={shortcutName} onChange={(event) => setShortcutName(event.target.value)} /></div></Dialog>
          <Dialog visible={Boolean(createPassDialog)} onHide={() => setCreatePassDialog(null)} className={`wallet-create-pass-dialog wallet-create-pass-dialog-${createPassDialog && createPassDialog.severity}`} footer={<Button label="OK" icon="pi pi-check" onClick={() => setCreatePassDialog(null)} />}>
            {createPassDialog && <div className="wallet-create-pass-dialog-content"><i className={createPassDialog.severity === 'success' ? 'pi pi-check-circle' : 'pi pi-times-circle'} aria-hidden="true" /><div><h5>{createPassDialog.severity === 'success' ? 'Pass created' : 'Pass not created'}</h5><p>{createPassDialog.message}</p></div></div>}
          </Dialog>
        </Container>
      </Card>
    </main>
  );
};

export default StandaloneWalletPassPortal;
